import { useEffect, useState } from "react";

import { ErrorHandler } from "@/web/lib/errors";
import { type AddressSuggestion, searchAddresses } from "@/web/lib/geocoding";

/**
 * Debounced Mapbox street-address search. Restricted to address features (no
 * POI/place search) since the user is targeting a specific building. Returns
 * the live suggestion list plus a fetching flag; the query is owned by the
 * caller so the input stays a controlled component.
 */
export function useAddressSearch({
  query,
  debounceMs = 350,
  minQueryLength = 3,
}: {
  query: string;
  debounceMs?: number;
  minQueryLength?: number;
}) {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < minQueryLength) {
      setSuggestions([]);
      setIsFetching(false);
      return;
    }

    const controller = new AbortController();
    setIsFetching(true);
    const timeout = setTimeout(() => {
      searchAddresses(trimmed, { signal: controller.signal })
        .then(setSuggestions)
        .catch((err) => {
          if (controller.signal.aborted) return;
          ErrorHandler.default(err);
        })
        .finally(() => {
          if (!controller.signal.aborted) setIsFetching(false);
        });
    }, debounceMs);

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [query, debounceMs, minQueryLength]);

  return { suggestions, isFetching };
}
